import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Video, FileText, MessageSquare, ClipboardList, ArrowRight, CheckCircle2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";

export function FacultyDashboardPage() {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/faculty/dashboard-summary");
      setTotals(data.data.totals);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <DashboardLayout title="Faculty overview"><LoadingBlock label="Loading dashboard…" /></DashboardLayout>;
  if (!totals) return null;
  return (
    <DashboardLayout title="Faculty overview" breadcrumbs={["Faculty", "Dashboard"]}>
      <div className="space-y-6" data-testid="faculty-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Assigned courses" value={totals.assigned_courses} icon="BookOpen" tone="navy" testId="faculty-kpi-courses" index={0} />
          <StatCard label="Total lessons" value={totals.total_lessons} icon="FileText" tone="green" testId="faculty-kpi-lessons" index={1} />
          <StatCard label="Videos uploaded" value={totals.videos_uploaded} icon="Video" tone="navy" testId="faculty-kpi-videos" index={2} />
          <StatCard label="Documents uploaded" value={totals.documents_uploaded} icon="FileCheck" tone="slate" testId="faculty-kpi-docs" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Draft lessons" value={totals.draft_lessons} icon="Edit3" tone="slate" testId="faculty-kpi-draft" index={4} />
          <StatCard label="Published lessons" value={totals.published_lessons} icon="CircleCheckBig" tone="green" testId="faculty-kpi-published" index={5} />
          <StatCard label="Live classes scheduled" value={totals.live_classes_scheduled} icon="Video" tone="navy" testId="faculty-kpi-live" index={6} />
          <StatCard label="Doubts pending" value={totals.doubts_pending} icon="MessageSquare" tone="amber" testId="faculty-kpi-doubts" index={7} />
        </section>
        <div className="grid gap-4 sm:grid-cols-3">
          <Link to="/app/faculty/courses" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md transition">
            <BookOpen className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold">Manage course content</div>
            <div className="text-xs text-slate-500">Upload videos, PDFs, PPTs, notes</div>
          </Link>
          <Link to="/app/faculty/live-classes" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md transition">
            <Video className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold">Schedule live class</div>
            <div className="text-xs text-slate-500">Add Zoom / Meet / Teams sessions</div>
          </Link>
          <Link to="/app/faculty/doubts" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#101876]/40 hover:shadow-md transition">
            <MessageSquare className="h-6 w-6 text-[#101876]" />
            <div className="mt-3 font-bold">Reply to doubts</div>
            <div className="text-xs text-slate-500">{totals.doubts_pending} open doubts</div>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function FacultyMyCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/faculty/my-courses");
        setCourses(data.data.courses);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <DashboardLayout title="My courses" breadcrumbs={["Faculty", "My courses"]}>
      <div className="space-y-4" data-testid="faculty-my-courses">
        {loading ? <LoadingBlock label="Loading courses…" /> : courses.length === 0 ? (
          <EmptyState title="No assigned courses" description="Steward Super Admin has not assigned you as trainer on any course yet." icon={BookOpen} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <div key={c.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition">
                {c.thumbnail ? (
                  <img src={resolveImg(c.thumbnail)} alt="" className="h-32 w-full object-cover" />
                ) : (
                  <div className="h-32 w-full bg-gradient-to-br from-[#101876] to-[#329112] flex items-center justify-center">
                    <BookOpen className="h-10 w-10 text-white/70" />
                  </div>
                )}
                <div className="flex-1 p-4 space-y-2">
                  <div className="text-sm font-bold text-slate-900 line-clamp-2">{c.course_title}</div>
                  <div className="text-xs text-slate-500 font-mono">{c.course_code}</div>
                  <div className="text-xs text-slate-500">{c.lessons_count || 0} lessons · {c.status}</div>
                </div>
                <Link to={`/app/faculty/courses/${c.id}/content`} className="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 text-xs font-bold text-[#101876] hover:bg-slate-100 flex items-center justify-between" data-testid={`faculty-course-open-${c.id}`}>
                  Manage content <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function FacultyDoubtsPage() {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/faculty/doubts"); setDoubts(data.data.doubts); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reply = async (id) => {
    const txt = replyText[id];
    if (!txt || !txt.trim()) return;
    try {
      await api.post(`/faculty/doubts/${id}/reply`, { faculty_reply: txt });
      toast.success("Reply posted.");
      setReplyText((p) => ({ ...p, [id]: "" }));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Student doubts" breadcrumbs={["Faculty", "Doubts"]}>
      <div className="space-y-4" data-testid="faculty-doubts">
        {loading ? <LoadingBlock label="Loading doubts…" /> : doubts.length === 0 ? (
          <EmptyState title="No doubts yet" description="You'll see doubts from learners of your courses here." icon={MessageSquare} />
        ) : doubts.map((d) => (
          <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid={`faculty-doubt-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-slate-900">{d.user?.full_name} <span className="text-xs font-normal text-slate-500">· {d.user?.email}</span></div>
                <div className="mt-0.5 text-xs text-slate-500">Lesson: {d.lesson?.lesson_title || "—"}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${d.status === "answered" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{d.status}</span>
            </div>
            <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 text-sm text-slate-800 whitespace-pre-line">{d.doubt_text}</div>
            {d.faculty_reply ? (
              <div className="rounded-md border-l-2 border-[#329112] bg-emerald-50/40 pl-3 py-2 text-sm text-slate-800 whitespace-pre-line">{d.faculty_reply}</div>
            ) : (
              <div className="flex items-start gap-2">
                <textarea rows={2} className="flex-1 rounded-md border border-slate-200 p-2 text-sm" placeholder="Write a reply…"
                  value={replyText[d.id] || ""} onChange={(e) => setReplyText((p) => ({ ...p, [d.id]: e.target.value }))}
                  data-testid={`faculty-reply-input-${d.id}`} />
                <button onClick={() => reply(d.id)} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid={`faculty-reply-submit-${d.id}`}>Reply</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
